import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { getPlayApiBaseUrl } from '../services/apiBaseUrl';

const PLAY_TOKEN_KEY = 'play_auth_token';
const PLAY_USER_KEY = 'play_user';

const PLAY_API_BASE = getPlayApiBaseUrl();

async function playRequest(path, { method = 'GET', body, token } = {}) {
  const response = await fetch(`${PLAY_API_BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.message || `Request failed (${response.status})`);
  return data;
}

function TopBar({ onLogout, user }) {
  return (
    <div className="border-b border-slate-200 bg-white">
      <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Groundaale Play</h1>
          <p className="text-xs text-slate-500">Standalone player flow</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-600">{user?.name || user?.phone || 'Player'}</span>
          <button
            className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
            onClick={onLogout}
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  );
}

function PlayAuth({ onAuthed }) {
  const [step, setStep] = useState('request');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [profile, setProfile] = useState({ name: '', city: '', role: 'PLAYER' });
  const [token, setToken] = useState('');

  const normalizedPhone = useMemo(() => phone.replace(/\D/g, ''), [phone]);

  const requestOtp = async (e) => {
    e.preventDefault();
    setError('');
    try {
      setLoading(true);
      await playRequest('/auth/request-otp', {
        method: 'POST',
        body: { phone: normalizedPhone },
      });
      setStep('verify');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    try {
      setLoading(true);
      const data = await playRequest('/auth/verify-otp', {
        method: 'POST',
        body: { phone: normalizedPhone, otp },
      });
      const nextToken = data?.data?.token;
      const nextUser = data?.data?.user;
      if (!nextToken || !nextUser) throw new Error('Invalid verify response');

      if (nextUser.isOnboarded) {
        onAuthed(nextToken, nextUser);
        return;
      }
      setToken(nextToken);
      setProfile((p) => ({ ...p, name: nextUser.name || '', city: nextUser.city || '' }));
      setStep('onboarding');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const finishOnboarding = async (e) => {
    e.preventDefault();
    setError('');
    try {
      setLoading(true);
      const data = await playRequest('/profile', {
        method: 'PUT',
        token,
        body: {
          name: profile.name,
          city: profile.city,
          role: profile.role,
        },
      });
      onAuthed(token, data?.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto mt-10 max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
      <h2 className="text-2xl font-semibold text-slate-900">Sign in to Play</h2>
      <p className="mt-1 text-sm text-slate-500">OTP based login for players</p>

      {error ? <div className="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div> : null}

      {step === 'request' && (
        <form className="mt-5 space-y-3" onSubmit={requestOtp}>
          <input
            className="w-full rounded-md border border-slate-300 px-3 py-2"
            placeholder="Phone (10 digits)"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-md bg-emerald-600 px-3 py-2 font-medium text-white hover:bg-emerald-700 disabled:opacity-70"
          >
            {loading ? 'Sending OTP...' : 'Request OTP'}
          </button>
        </form>
      )}

      {step === 'verify' && (
        <form className="mt-5 space-y-3" onSubmit={verifyOtp}>
          <input
            className="w-full rounded-md border border-slate-300 px-3 py-2"
            placeholder="Enter OTP"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
          />
          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-md bg-emerald-600 px-3 py-2 font-medium text-white hover:bg-emerald-700 disabled:opacity-70"
          >
            {loading ? 'Verifying...' : 'Verify OTP'}
          </button>
        </form>
      )}

      {step === 'onboarding' && (
        <form className="mt-5 space-y-3" onSubmit={finishOnboarding}>
          <input
            className="w-full rounded-md border border-slate-300 px-3 py-2"
            placeholder="Name"
            value={profile.name}
            onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))}
          />
          <input
            className="w-full rounded-md border border-slate-300 px-3 py-2"
            placeholder="City"
            value={profile.city}
            onChange={(e) => setProfile((p) => ({ ...p, city: e.target.value }))}
          />
          <select
            className="w-full rounded-md border border-slate-300 px-3 py-2"
            value={profile.role}
            onChange={(e) => setProfile((p) => ({ ...p, role: e.target.value }))}
          >
            <option value="PLAYER">Player</option>
            <option value="ORGANIZER">Organizer</option>
            <option value="BOTH">Both</option>
          </select>
          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-md bg-emerald-600 px-3 py-2 font-medium text-white hover:bg-emerald-700 disabled:opacity-70"
          >
            {loading ? 'Saving profile...' : 'Complete Onboarding'}
          </button>
        </form>
      )}
    </div>
  );
}

function DiscoveryPage({ token }) {
  const [grounds, setGrounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await playRequest('/discovery/grounds', { token });
        if (alive) setGrounds(data?.data || []);
      } catch (err) {
        if (alive) setError(err.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  if (loading) return <p className="p-4 text-slate-600">Loading grounds...</p>;
  if (error) return <p className="p-4 text-red-700">{error}</p>;

  return (
    <div className="mx-auto max-w-5xl p-4">
      <h2 className="text-2xl font-semibold text-slate-900">Discover Grounds</h2>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        {grounds.map((g) => (
          <button
            key={g.id}
            className="rounded-lg border border-slate-200 bg-white p-4 text-left hover:border-emerald-300"
            onClick={() => navigate(`/play/ground/${g.facility_id}/${g.id}`)}
          >
            <h3 className="font-semibold text-slate-900">{g.name}</h3>
            <p className="mt-1 text-sm text-slate-600">{g.facility_name || 'Facility'}</p>
            <p className="text-sm text-slate-500">{g.location || g.city || 'Location unavailable'}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

function GroundDetailPage({ token }) {
  const { facilityId, groundId } = useParams();
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await playRequest(
          `/discovery/slots?facility_id=${facilityId}&ground_id=${groundId}&date=${today}`,
          { token }
        );
        if (alive) setSlots(data?.data || []);
      } catch (err) {
        if (alive) setError(err.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [token, facilityId, groundId, today]);

  if (loading) return <p className="p-4 text-slate-600">Loading slots...</p>;
  if (error) return <p className="p-4 text-red-700">{error}</p>;

  return (
    <div className="mx-auto max-w-5xl p-4">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-slate-900">Available Slots</h2>
        <button className="text-sm text-emerald-700" onClick={() => navigate('/play/discovery')}>
          Back to discovery
        </button>
      </div>
      <p className="mb-4 text-sm text-slate-500">Date: {today}</p>
      <div className="grid gap-3">
        {slots.map((s) => {
          const isAvailable = Boolean(s.is_available);
          const timeSlot = `${String(s.start_time || '').slice(0, 5)}-${String(s.end_time || '').slice(0, 5)}`;
          return (
            <div key={s.id} className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-slate-900">{s.name || `Slot #${s.id}`}</h3>
                <span className={`rounded-full px-2 py-1 text-xs ${isAvailable ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                  {isAvailable ? 'Available' : 'Unavailable'}
                </span>
              </div>
              <p className="mt-1 text-sm text-slate-600">{timeSlot}</p>
              <p className="text-sm text-slate-500">Queue: {s.queue_depth}</p>
              <button
                disabled={!isAvailable}
                onClick={() =>
                  navigate('/play/confirm', {
                    state: {
                      facility_id: Number(facilityId),
                      ground_id: Number(groundId),
                      service_id: Number(s.service_id),
                      configured_slot_id: Number(s.id),
                      slot_name: s.name || `Slot #${s.id}`,
                      time_slot: timeSlot,
                      date: today,
                    },
                  })
                }
                className="mt-3 rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                Continue
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BookingConfirmPage({ token, user }) {
  const location = useLocation();
  const navigate = useNavigate();
  const data = location.state || null;
  const [contactName, setContactName] = useState(user?.name || '');
  const [contactPhone, setContactPhone] = useState(user?.phone || '');
  const [specialRequests, setSpecialRequests] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!data) return <Navigate to="/play/discovery" replace />;

  return (
    <div className="mx-auto max-w-xl p-4">
      <h2 className="text-2xl font-semibold text-slate-900">Confirm Booking Request</h2>
      <div className="mt-3 rounded-md border border-slate-200 bg-white p-4 text-sm text-slate-600">
        <p>Facility: {data.facility_id}</p>
        <p>Ground: {data.ground_id}</p>
        <p>Slot: {data.slot_name}</p>
        <p>Date: {data.date}</p>
      </div>
      {error ? <p className="mt-3 text-sm text-red-700">{error}</p> : null}
      <form
        className="mt-4 space-y-3"
        onSubmit={async (e) => {
          e.preventDefault();
          setError('');
          try {
            setLoading(true);
            const payload = {
              ...data,
              booking_type: 'single',
              contact_name: contactName,
              contact_phone: String(contactPhone || '').replace(/\D/g, ''),
              special_requests: specialRequests || undefined,
            };
            const resp = await playRequest('/bookings/request', {
              method: 'POST',
              token,
              body: payload,
            });
            navigate('/play/success', { state: { reference: resp?.data?.reference, date: data.date, slot_name: data.slot_name } });
          } catch (err) {
            setError(err.message);
          } finally {
            setLoading(false);
          }
        }}
      >
        <input
          className="w-full rounded-md border border-slate-300 px-3 py-2"
          value={contactName}
          onChange={(e) => setContactName(e.target.value)}
          placeholder="Contact name"
        />
        <input
          className="w-full rounded-md border border-slate-300 px-3 py-2"
          value={contactPhone}
          onChange={(e) => setContactPhone(e.target.value)}
          placeholder="Contact phone"
        />
        <textarea
          className="w-full rounded-md border border-slate-300 px-3 py-2"
          rows={3}
          value={specialRequests}
          onChange={(e) => setSpecialRequests(e.target.value)}
          placeholder="Special requests (optional)"
        />
        <button
          type="submit"
          disabled={loading}
          className="w-full rounded-md bg-emerald-600 px-3 py-2 font-medium text-white disabled:opacity-70"
        >
          {loading ? 'Submitting...' : 'Submit for approval'}
        </button>
      </form>
    </div>
  );
}

function BookingSuccessPage() {
  const location = useLocation();
  const info = location.state || {};
  return (
    <div className="mx-auto mt-8 max-w-xl rounded-lg border border-slate-200 bg-white p-6">
      <h2 className="text-2xl font-semibold text-slate-900">Request submitted</h2>
      <p className="mt-1 text-slate-600">Your booking request is pending partner approval.</p>
      <div className="mt-4 rounded-md bg-slate-50 p-4 text-sm text-slate-700">
        <p>Reference: {info.reference || 'Pending'}</p>
        <p>Date: {info.date || '-'}</p>
        <p>Slot: {info.slot_name || '-'}</p>
      </div>
    </div>
  );
}

function BidQueuePage({ token }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await playRequest('/notifications', { token });
        if (alive) setNotifications(data?.data || []);
      } catch (err) {
        if (alive) setError(err.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  if (loading) return <p className="p-4 text-slate-600">Loading updates...</p>;
  if (error) return <p className="p-4 text-red-700">{error}</p>;

  return (
    <div className="mx-auto max-w-4xl p-4">
      <h2 className="text-2xl font-semibold text-slate-900">Bid Queue & Updates</h2>
      <div className="mt-4 grid gap-3">
        {notifications.length === 0 && (
          <div className="rounded-md border border-slate-200 bg-white p-4 text-slate-600">No updates yet.</div>
        )}
        {notifications.map((n) => (
          <div key={n.id} className="rounded-md border border-slate-200 bg-white p-4">
            <h3 className="font-semibold text-slate-900">{n.title || 'Notification'}</h3>
            <p className="mt-1 text-sm text-slate-600">{n.body || ''}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PlayApp() {
  const [token, setToken] = useState(() => localStorage.getItem(PLAY_TOKEN_KEY));
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem(PLAY_USER_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (_e) {
      return null;
    }
  });

  const onAuthed = (nextToken, nextUser) => {
    localStorage.setItem(PLAY_TOKEN_KEY, nextToken);
    localStorage.setItem(PLAY_USER_KEY, JSON.stringify(nextUser || {}));
    setToken(nextToken);
    setUser(nextUser || null);
  };

  const logout = () => {
    localStorage.removeItem(PLAY_TOKEN_KEY);
    localStorage.removeItem(PLAY_USER_KEY);
    setToken(null);
    setUser(null);
  };

  if (!token) return <PlayAuth onAuthed={onAuthed} />;

  return (
    <div className="min-h-screen bg-slate-50">
      <TopBar onLogout={logout} user={user} />
      <Routes>
        <Route index element={<Navigate to="discovery" replace />} />
        <Route path="discovery" element={<DiscoveryPage token={token} />} />
        <Route path="ground/:facilityId/:groundId" element={<GroundDetailPage token={token} />} />
        <Route path="confirm" element={<BookingConfirmPage token={token} user={user} />} />
        <Route path="success" element={<BookingSuccessPage />} />
        <Route path="bids" element={<BidQueuePage token={token} />} />
        <Route path="*" element={<Navigate to="discovery" replace />} />
      </Routes>
    </div>
  );
}
